import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, FileSpreadsheet, Check, X, Loader2, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportInventory = () => {
  const { getAuthHeader } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const expectedColumns = ['name', 'quantity', 'buy_price', 'sell_price', 'category', 'sku', 'barcode'];

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return { headers: [], data: [] };

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    const data = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/['"]/g, ''));
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });

    return { headers, data };
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const { headers, data } = parseCSV(event.target.result);
      setHeaders(headers);
      setPreview(data.slice(0, 10)); // Show first 10 rows
    };
    reader.readAsText(selectedFile);
  };

  const validateData = () => {
    const requiredFields = ['name', 'quantity', 'buy_price', 'sell_price'];
    const missingFields = requiredFields.filter(field => !headers.includes(field));
    
    if (missingFields.length > 0) {
      toast.error(`Missing required columns: ${missingFields.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleImport = async () => {
    if (!file || preview.length === 0) {
      toast.error('Please upload a CSV file first');
      return;
    }

    if (!validateData()) return;

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const { data } = parseCSV(event.target.result);
        
        let successCount = 0;
        let failCount = 0;
        const errors = [];

        for (const row of data) {
          try {
            const productData = {
              name: row.name,
              quantity: parseInt(row.quantity) || 0,
              buy_price: parseFloat(row.buy_price) || 0,
              sell_price: parseFloat(row.sell_price) || 0,
              category: row.category || 'General',
              sku: row.sku || '',
              barcode: row.barcode || ''
            };

            await axios.post(`${API_URL}/products`, productData, { headers: getAuthHeader() });
            successCount++;
          } catch (error) {
            failCount++;
            errors.push({ row: row.name, error: error.response?.data?.detail || 'Failed to import' });
          }
        }

        setImportResult({ successCount, failCount, errors, total: data.length });
        
        if (failCount === 0) {
          toast.success(`Successfully imported ${successCount} products!`);
        } else {
          toast.warning(`Imported ${successCount} products, ${failCount} failed`);
        }

        setImporting(false);
      };
      reader.readAsText(file);
    } catch (error) {
      toast.error('Import failed');
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'name,quantity,buy_price,sell_price,category,sku,barcode\nWhey Protein 2kg,50,45.00,79.99,Protein,WP-001,123456789012\nCreatine Monohydrate,30,15.00,29.99,Performance,CR-001,123456789013\nMultivitamin 60caps,100,8.00,19.99,Vitamins,MV-001,123456789014';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const clearFile = () => {
    setFile(null);
    setPreview([]);
    setHeaders([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="import-inventory-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
          Import Inventory
        </h1>
        <p className="text-slate-500 mt-1">Bulk upload products from a CSV file</p>
      </div>

      {/* Instructions Card */}
      <Card className="border rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-5 h-5 text-orange-500" />
            CSV Format Instructions
          </CardTitle>
          <CardDescription>
            Your CSV file should have the following columns:
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {expectedColumns.map((col, idx) => (
              <div key={col} className={`p-3 rounded-sm ${idx < 4 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50 border border-slate-200'}`}>
                <p className="font-medium text-sm">{col}</p>
                <p className="text-xs text-slate-500">{idx < 4 ? 'Required' : 'Optional'}</p>
              </div>
            ))}
          </div>
          <Button onClick={downloadTemplate} variant="outline" data-testid="download-template">
            <Download className="w-4 h-4 mr-2" /> Download Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Card */}
      <Card className="border rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="w-5 h-5 text-orange-500" />
            Upload CSV File
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-orange-300'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="w-10 h-10 text-emerald-500" />
                  <div className="text-left">
                    <p className="font-medium text-emerald-700">{file.name}</p>
                    <p className="text-sm text-emerald-600">{preview.length} rows detected</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); clearFile(); }}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-600 font-medium">Click to upload or drag and drop</p>
                  <p className="text-sm text-slate-400 mt-1">CSV files only</p>
                </>
              )}
            </div>

            {preview.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Preview (first 10 rows)</h3>
                <div className="overflow-x-auto border rounded-sm">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        {headers.map((header) => (
                          <TableHead key={header} className="text-xs uppercase font-bold">
                            {header}
                            {['name', 'quantity', 'buy_price', 'sell_price'].includes(header) && (
                              <span className="text-orange-500 ml-1">*</span>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.map((row, idx) => (
                        <TableRow key={idx}>
                          {headers.map((header) => (
                            <TableCell key={header} className="text-sm">
                              {row[header] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleImport}
                    disabled={importing}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="import-button"
                  >
                    {importing ? (
                      <><Loader2 className="w-4 h-4 mr-2 spinner" /> Importing...</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Import {preview.length} Products</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={clearFile}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Import Result */}
            {importResult && (
              <Card className={`${importResult.failCount === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    {importResult.failCount === 0 ? (
                      <Check className="w-8 h-8 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-amber-500" />
                    )}
                    <div>
                      <p className="font-medium text-lg">
                        Import Complete: {importResult.successCount}/{importResult.total} products
                      </p>
                      {importResult.failCount > 0 && (
                        <p className="text-sm text-amber-600">
                          {importResult.failCount} products failed to import
                        </p>
                      )}
                    </div>
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="mt-4 space-y-1">
                      <p className="text-sm font-medium text-amber-700">Errors:</p>
                      {importResult.errors.slice(0, 5).map((err, idx) => (
                        <p key={idx} className="text-sm text-amber-600">
                          • {err.row}: {err.error}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportInventory;
